import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/components/ui/sonner';
import { 
  Image as ImageIcon, 
  FileJson, 
  Download, 
  Sparkles, 
  Copy, 
  CheckCircle2,
  RefreshCw,
  Eye,
  Palette,
  Type,
  Users,
  Layout,
  Zap,
  FileText,
  Paintbrush
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Get proxied image URL to bypass CORS
 */
const getProxiedImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  return `${API}/proxy-image?url=${encodeURIComponent(imageUrl)}`;
};

/**
 * Color swatch component
 */
const ColorSwatch = ({ color, onCopy }) => {
  const handleClick = () => {
    navigator.clipboard.writeText(color.hex);
    toast.success(`${color.hex} kopiert!`);
    if (onCopy) onCopy(color.hex);
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={handleClick}
          className="group flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-muted/50 transition-colors"
          data-testid={`color-swatch-${color.hex?.replace('#', '')}`}
        >
          <div 
            className="w-10 h-10 rounded-lg border shadow-sm group-hover:scale-110 transition-transform"
            style={{ backgroundColor: color.hex }}
          />
          <span className="text-xs text-muted-foreground font-mono">{color.hex}</span>
          <span className="text-xs text-muted-foreground truncate max-w-[80px]">{color.name}</span>
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <p>Klicken zum Kopieren: {color.hex}</p>
        <p className="text-muted-foreground">{color.name}</p>
      </TooltipContent>
    </Tooltip>
  );
};

/**
 * Style Analysis Section Component
 */
const StyleAnalysisSection = ({ styleAnalysis, onCopyInstructions }) => {
  if (!styleAnalysis) return null;

  return (
    <div className="space-y-4" data-testid="thumbnail-style-analysis">
      {/* Color Palette */}
      {styleAnalysis.palette?.length > 0 && (
        <div className="p-4 rounded-lg bg-muted/30 border" data-testid="style-palette-section">
          <div className="flex items-center gap-2 mb-3">
            <Palette className="h-4 w-4 text-primary" />
            <h4 className="font-medium text-sm">Farbpalette</h4>
            {styleAnalysis.color_mood && (
              <Badge variant="secondary" className="text-xs">{styleAnalysis.color_mood}</Badge>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {styleAnalysis.palette.map((color, idx) => (
              <ColorSwatch key={idx} color={color} />
            ))}
          </div>
        </div>
      )}

      {/* Text Style */}
      {styleAnalysis.text?.presence && (
        <div className="p-4 rounded-lg bg-muted/30 border" data-testid="style-text-section">
          <div className="flex items-center gap-2 mb-3">
            <Type className="h-4 w-4 text-primary" />
            <h4 className="font-medium text-sm">Text-Stil</h4>
          </div>
          <div className="space-y-2">
            {styleAnalysis.text.texts?.length > 0 && (
              <div>
                <span className="text-xs text-muted-foreground">Sichtbare Texte:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {styleAnalysis.text.texts.map((text, idx) => (
                    <Badge key={idx} variant="outline" className="font-mono text-xs">{text}</Badge>
                  ))}
                </div>
              </div>
            )}
            {styleAnalysis.text.approx_font_style && (
              <div>
                <span className="text-xs text-muted-foreground">Schriftart-Stil: </span>
                <span className="text-sm">{styleAnalysis.text.approx_font_style}</span>
              </div>
            )}
            <div className="flex flex-wrap gap-4">
              {styleAnalysis.text.sizes?.length > 0 && (
                <div>
                  <span className="text-xs text-muted-foreground">Größen: </span>
                  <span className="text-sm">{styleAnalysis.text.sizes.join(', ')}</span>
                </div>
              )}
              {styleAnalysis.text.positions?.length > 0 && (
                <div>
                  <span className="text-xs text-muted-foreground">Positionen: </span>
                  <span className="text-sm">{styleAnalysis.text.positions.join(', ')}</span>
                </div>
              )}
            </div>
            {styleAnalysis.text.colors?.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Text-Farben:</span>
                {styleAnalysis.text.colors.map((color, idx) => (
                  <Tooltip key={idx}>
                    <TooltipTrigger asChild>
                      <button 
                        onClick={() => {
                          navigator.clipboard.writeText(color);
                          toast.success(`${color} kopiert!`);
                        }}
                        className="w-5 h-5 rounded border cursor-pointer hover:scale-110 transition-transform"
                        style={{ backgroundColor: color }}
                        data-testid={`text-color-${color?.replace('#', '')}`}
                      />
                    </TooltipTrigger>
                    <TooltipContent>{color}</TooltipContent>
                  </Tooltip>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Faces/People */}
      {styleAnalysis.faces?.count > 0 && (
        <div className="p-4 rounded-lg bg-muted/30 border" data-testid="style-faces-section">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-primary" />
            <h4 className="font-medium text-sm">Personen/Gesichter</h4>
            <Badge variant="secondary" className="text-xs">{styleAnalysis.faces.count}</Badge>
          </div>
          <div className="space-y-2 text-sm">
            {styleAnalysis.faces.positions?.length > 0 && (
              <div>
                <span className="text-xs text-muted-foreground">Positionen: </span>
                <span>{styleAnalysis.faces.positions.join(', ')}</span>
              </div>
            )}
            {styleAnalysis.faces.expressions?.length > 0 && (
              <div>
                <span className="text-xs text-muted-foreground">Ausdruck: </span>
                <span>{styleAnalysis.faces.expressions.join(', ')}</span>
              </div>
            )}
            {styleAnalysis.faces.prominence && (
              <div>
                <span className="text-xs text-muted-foreground">Prominenz: </span>
                <span>{styleAnalysis.faces.prominence}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Composition */}
      {styleAnalysis.composition && (
        <div className="p-4 rounded-lg bg-muted/30 border" data-testid="style-composition-section">
          <div className="flex items-center gap-2 mb-3">
            <Layout className="h-4 w-4 text-primary" />
            <h4 className="font-medium text-sm">Komposition</h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            {styleAnalysis.composition.layout && (
              <div>
                <span className="text-xs text-muted-foreground">Layout: </span>
                <span>{styleAnalysis.composition.layout}</span>
              </div>
            )}
            {styleAnalysis.composition.thirds_usage && (
              <div>
                <span className="text-xs text-muted-foreground">Drittel-Regel: </span>
                <span>{styleAnalysis.composition.thirds_usage}</span>
              </div>
            )}
            {styleAnalysis.composition.focal_points?.length > 0 && (
              <div className="col-span-2">
                <span className="text-xs text-muted-foreground">Fokuspunkte: </span>
                <span>{styleAnalysis.composition.focal_points.join(', ')}</span>
              </div>
            )}
            {styleAnalysis.composition.depth && (
              <div>
                <span className="text-xs text-muted-foreground">Tiefe: </span>
                <span>{styleAnalysis.composition.depth}</span>
              </div>
            )}
            {styleAnalysis.composition.whitespace && (
              <div>
                <span className="text-xs text-muted-foreground">Weißraum: </span>
                <span>{styleAnalysis.composition.whitespace}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Decoration Elements & Attention Grabbers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {styleAnalysis.elements?.length > 0 && (
          <div className="p-4 rounded-lg bg-muted/30 border" data-testid="style-elements-section">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <h4 className="font-medium text-sm">Deko-Elemente</h4>
            </div>
            <div className="flex flex-wrap gap-1">
              {styleAnalysis.elements.map((elem, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">{elem}</Badge>
              ))}
            </div>
          </div>
        )}
        
        {styleAnalysis.attention_grabbers?.length > 0 && (
          <div className="p-4 rounded-lg bg-muted/30 border" data-testid="style-attention-section">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="h-4 w-4 text-accent" />
              <h4 className="font-medium text-sm">Aufmerksamkeits-Faktoren</h4>
            </div>
            <div className="flex flex-wrap gap-1">
              {styleAnalysis.attention_grabbers.map((elem, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs bg-accent/10 text-accent">{elem}</Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Nachbauen Instructions */}
      {styleAnalysis.instructions_de && (
        <div className="p-4 rounded-lg bg-primary/5 border border-primary/20" data-testid="style-instructions-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Paintbrush className="h-4 w-4 text-primary" />
              <h4 className="font-medium text-sm">Nachbauen-Anleitung</h4>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onCopyInstructions}
              data-testid="copy-instructions-button"
            >
              <Copy className="h-4 w-4 mr-1" />
              Kopieren
            </Button>
          </div>
          <div className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
            {styleAnalysis.instructions_de}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * ThumbnailCard - Displays thumbnail with AI description, style analysis, JSON export, and download options
 */
export function ThumbnailCard({ analysisId, thumbnail, onUpdate }) {
  const [isDescribing, setIsDescribing] = useState(false);
  const [isAnalyzingStyle, setIsAnalyzingStyle] = useState(false);
  const [localThumbnail, setLocalThumbnail] = useState(thumbnail);
  const [showJson, setShowJson] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [activeTab, setActiveTab] = useState('preview');

  const handleDescribeThumbnail = async () => {
    if (!analysisId) return;
    
    setIsDescribing(true);
    try {
      const response = await axios.post(`${API}/analyses/${analysisId}/describe-thumbnail`);
      const newThumbnail = response.data.thumbnail;
      setLocalThumbnail(newThumbnail);
      if (onUpdate) onUpdate(newThumbnail);
      toast.success('Thumbnail-Beschreibung erstellt!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Beschreibung fehlgeschlagen';
      toast.error(errorMsg);
    } finally {
      setIsDescribing(false);
    }
  };

  const handleAnalyzeStyle = async () => {
    if (!analysisId) return;
    
    setIsAnalyzingStyle(true);
    try {
      const response = await axios.post(`${API}/analyses/${analysisId}/thumbnail-style`);
      const styleAnalysis = response.data.style_analysis;
      const newThumbnail = {
        ...localThumbnail,
        url: response.data.thumbnail_url || localThumbnail?.url,
        style_analysis: styleAnalysis
      };
      setLocalThumbnail(newThumbnail);
      if (onUpdate) onUpdate(newThumbnail);
      setActiveTab('style'); // Switch to style tab after analysis
      toast.success('Thumbnail-Stil analysiert!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Stil-Analyse fehlgeschlagen';
      toast.error(errorMsg);
    } finally {
      setIsAnalyzingStyle(false);
    }
  };

  const handleCopyJson = () => {
    if (localThumbnail?.json_description) {
      navigator.clipboard.writeText(JSON.stringify(localThumbnail.json_description, null, 2));
      toast.success('JSON in Zwischenablage kopiert!');
    }
  };

  const handleCopyStyleJson = () => {
    if (localThumbnail?.style_analysis) {
      navigator.clipboard.writeText(JSON.stringify(localThumbnail.style_analysis, null, 2));
      toast.success('Stil-JSON in Zwischenablage kopiert!');
    }
  };

  const handleCopyInstructions = () => {
    if (localThumbnail?.style_analysis?.instructions_de) {
      navigator.clipboard.writeText(localThumbnail.style_analysis.instructions_de);
      toast.success('Anleitung in Zwischenablage kopiert!');
    }
  };

  const handleDownload = async () => {
    if (!localThumbnail?.url) return;
    
    try {
      // Use proxied URL for download
      const proxyUrl = getProxiedImageUrl(localThumbnail.url);
      window.open(proxyUrl, '_blank');
      toast.success('Thumbnail wird in neuem Tab geöffnet');
    } catch (err) {
      toast.error('Download fehlgeschlagen');
    }
  };

  const handleCopyDescription = () => {
    if (localThumbnail?.description) {
      navigator.clipboard.writeText(localThumbnail.description);
      toast.success('Beschreibung kopiert!');
    }
  };

  if (!localThumbnail?.url) {
    return null;
  }

  const hasStyleAnalysis = localThumbnail?.style_analysis;

  return (
    <Card className="overflow-hidden" data-testid="thumbnail-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Video Thumbnail</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {localThumbnail.description && (
              <Badge variant="secondary" className="bg-green-500/10 text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Beschrieben
              </Badge>
            )}
            {hasStyleAnalysis && (
              <Badge variant="secondary" className="bg-primary/10 text-primary">
                <Palette className="h-3 w-3 mr-1" />
                Stil analysiert
              </Badge>
            )}
          </div>
        </div>
        <CardDescription>
          Thumbnail-Vorschau mit KI-gestützter Analyse
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Tabs for Preview / Description / Style */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start">
            <TabsTrigger value="preview" data-testid="tab-preview">
              <ImageIcon className="h-4 w-4 mr-1" />
              Vorschau
            </TabsTrigger>
            {localThumbnail.description && (
              <TabsTrigger value="description" data-testid="tab-description">
                <Eye className="h-4 w-4 mr-1" />
                Beschreibung
              </TabsTrigger>
            )}
            {hasStyleAnalysis && (
              <TabsTrigger value="style" data-testid="tab-style">
                <Palette className="h-4 w-4 mr-1" />
                Stil-Analyse
              </TabsTrigger>
            )}
          </TabsList>

          {/* Preview Tab */}
          <TabsContent value="preview" className="mt-4">
            {/* Thumbnail Image */}
            <div className="relative aspect-video rounded-lg overflow-hidden bg-muted">
              {!imageError ? (
                <img 
                  src={getProxiedImageUrl(localThumbnail.url)} 
                  alt="Video Thumbnail"
                  className="w-full h-full object-cover"
                  data-testid="thumbnail-image"
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center flex-col gap-2 text-muted-foreground">
                  <ImageIcon className="h-12 w-12" />
                  <span className="text-sm">Bild konnte nicht geladen werden</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDescribeThumbnail}
                disabled={isDescribing || isAnalyzingStyle}
                data-testid="describe-thumbnail-button"
              >
                {isDescribing ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Beschreibe...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    {localThumbnail.description ? 'Neu beschreiben' : 'KI-Beschreibung'}
                  </>
                )}
              </Button>

              <Button
                variant={hasStyleAnalysis ? "outline" : "default"}
                size="sm"
                onClick={handleAnalyzeStyle}
                disabled={isDescribing || isAnalyzingStyle}
                data-testid="analyze-style-button"
              >
                {isAnalyzingStyle ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Analysiere Stil...
                  </>
                ) : (
                  <>
                    <Palette className="h-4 w-4 mr-2" />
                    {hasStyleAnalysis ? 'Stil neu analysieren' : 'Thumbnail analysieren'}
                  </>
                )}
              </Button>

              {localThumbnail.json_description && (
                <Dialog open={showJson} onOpenChange={setShowJson}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="show-json-button">
                      <FileJson className="h-4 w-4 mr-2" />
                      JSON
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh]">
                    <DialogHeader>
                      <DialogTitle>Thumbnail JSON-Beschreibung</DialogTitle>
                      <DialogDescription>
                        Strukturierte Analyse des Thumbnails
                      </DialogDescription>
                    </DialogHeader>
                    <div className="relative">
                      <Button
                        variant="outline"
                        size="sm"
                        className="absolute top-2 right-2 z-10"
                        onClick={handleCopyJson}
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        Kopieren
                      </Button>
                      <ScrollArea className="h-[400px] w-full rounded-md border p-4">
                        <pre className="text-xs font-mono whitespace-pre-wrap">
                          {JSON.stringify(localThumbnail.json_description, null, 2)}
                        </pre>
                      </ScrollArea>
                    </div>
                  </DialogContent>
                </Dialog>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                data-testid="download-thumbnail-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>

            {/* Loading State */}
            {(isDescribing || isAnalyzingStyle) && (
              <div className="space-y-2 mt-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-5/6" />
              </div>
            )}
          </TabsContent>

          {/* Description Tab */}
          {localThumbnail.description && (
            <TabsContent value="description" className="mt-4">
              <div className="relative p-4 rounded-lg bg-muted/50 border" data-testid="thumbnail-description">
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={handleCopyDescription}
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Eye className="h-4 w-4 text-primary" />
                  Klartext-Beschreibung
                </h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {localThumbnail.description}
                </p>
              </div>

              {/* Quick JSON Info */}
              {localThumbnail.json_description && (
                <Accordion type="single" collapsible className="mt-4">
                  <AccordionItem value="json">
                    <AccordionTrigger className="text-sm">
                      <div className="flex items-center gap-2">
                        <FileJson className="h-4 w-4" />
                        JSON-Details anzeigen
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="relative">
                        <Button
                          variant="outline"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={handleCopyJson}
                        >
                          <Copy className="h-4 w-4 mr-1" />
                        </Button>
                        <pre className="text-xs font-mono whitespace-pre-wrap p-4 bg-muted rounded-lg overflow-x-auto">
                          {JSON.stringify(localThumbnail.json_description, null, 2)}
                        </pre>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              )}
            </TabsContent>
          )}

          {/* Style Analysis Tab */}
          {hasStyleAnalysis && (
            <TabsContent value="style" className="mt-4">
              <StyleAnalysisSection 
                styleAnalysis={localThumbnail.style_analysis} 
                onCopyInstructions={handleCopyInstructions}
              />

              {/* Export Style JSON */}
              <div className="flex justify-end mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyStyleJson}
                  data-testid="copy-style-json-button"
                >
                  <FileJson className="h-4 w-4 mr-2" />
                  Stil-JSON exportieren
                </Button>
              </div>
            </TabsContent>
          )}
        </Tabs>
      </CardContent>
    </Card>
  );
}

export default ThumbnailCard;
